import { useEffect, useState } from "react";
import { useTheme } from "./useTheme";

function useElapsed(since) {
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    if (!since) return;
    const id = setInterval(() => setElapsed(Math.floor((Date.now() - since) / 1000)), 10000);
    setElapsed(Math.floor((Date.now() - since) / 1000));
    return () => clearInterval(id);
  }, [since]);
  return elapsed;
}

function formatElapsed(seconds) {
  if (seconds < 60) return "меньше минуты";
  const m = Math.floor(seconds / 60);
  if (m < 60) return `${m} мин`;
  const h = Math.floor(m / 60);
  return `${h} ч ${m % 60} мин`;
}

export default function MaintenanceOverlay() {
  const [isDown, setIsDown] = useState(false);
  const [since, setSince] = useState(null);
  const [visible, setVisible] = useState(false);
  const { isDark } = useTheme();

  useEffect(() => {
    const onDown = (e) => {
      setSince(e.detail?.ts ?? Date.now());
      setIsDown(true);
      setVisible(true);
    };
    const onUp = () => {
      setIsDown(false);
      // keep visible for 1.5s so fade-out plays
      setTimeout(() => setVisible(false), 1500);
    };
    window.addEventListener("audassist:backend-down", onDown);
    window.addEventListener("audassist:backend-up", onUp);
    return () => {
      window.removeEventListener("audassist:backend-down", onDown);
      window.removeEventListener("audassist:backend-up", onUp);
    };
  }, []);

  const elapsed = useElapsed(since);

  if (!visible) return null;

  const bg = isDark ? "rgba(10,10,12,0.90)" : "rgba(248,250,252,0.92)";
  const card = isDark ? "#1c1c1e" : "#ffffff";
  const cardBorder = isDark ? "1px solid rgba(255,255,255,0.08)" : "1px solid rgba(226,232,240,0.8)";
  const cardShadow = isDark
    ? "0 32px 80px -24px rgba(0,0,0,0.9), 0 0 0 1px rgba(255,255,255,0.04)"
    : "0 32px 80px -24px rgba(15,23,42,0.22)";
  const iconBg = isDark
    ? "linear-gradient(135deg, rgba(251,191,36,0.14), rgba(249,115,22,0.14))"
    : "linear-gradient(135deg, #fffbeb, #fff7ed)";
  const iconBorder = isDark ? "1px solid rgba(251,191,36,0.25)" : "1px solid #fef3c7";
  const iconColor = isDark ? "#fcd34d" : "#f59e0b";
  const iconShadow = isDark
    ? "0 16px 40px -20px rgba(251,191,36,0.5)"
    : "0 16px 40px -20px rgba(251,191,36,0.35)";
  const titleColor = isDark ? "#f5f5f7" : "#0f172a";
  const subColor = isDark ? "rgba(235,235,245,0.62)" : "#64748b";
  const chipBg = isDark ? "rgba(255,255,255,0.04)" : "#f8fafc";
  const chipBorder = isDark ? "1px solid rgba(255,255,255,0.08)" : "1px solid #e2e8f0";
  const chipColor = isDark ? "rgba(235,235,245,0.65)" : "#64748b";
  const okColor = isDark ? "#34d399" : "#059669";

  return (
    <div
      className={`fixed inset-0 z-[9999] flex items-center justify-center p-6 transition-opacity duration-700 ${isDown ? "opacity-100" : "opacity-0 pointer-events-none"}`}
      style={{ background: bg, backdropFilter: "blur(12px)", WebkitBackdropFilter: "blur(12px)" }}
    >
      <div
        className="flex max-w-sm flex-col items-center gap-5 rounded-[32px] px-8 py-10 text-center"
        style={{ background: card, border: cardBorder, boxShadow: cardShadow }}
      >
        <div
          className={`flex h-16 w-16 items-center justify-center rounded-3xl ${isDown ? "animate-pulse" : ""}`}
          style={{ background: iconBg, border: iconBorder, boxShadow: iconShadow }}
        >
          <svg viewBox="0 0 24 24" className="h-8 w-8" fill="none" stroke={iconColor} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />
          </svg>
        </div>

        <div className="space-y-1.5">
          <h2 className="text-[17px] font-semibold leading-snug" style={{ color: titleColor }}>
            Ведутся технические работы
          </h2>
          <p className="text-sm leading-relaxed" style={{ color: subColor }}>
            Сервер временно недоступен.<br />Обновление займёт несколько минут.
          </p>
        </div>

        {since && elapsed >= 0 && (
          <div
            className="flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs"
            style={{ background: chipBg, border: chipBorder, color: chipColor }}
          >
            <span className="h-1.5 w-1.5 rounded-full animate-pulse" style={{ background: iconColor }} />
            Уже {formatElapsed(elapsed)}
          </div>
        )}

        {!isDown && (
          <div className="flex items-center gap-2 text-sm font-medium" style={{ color: okColor }}>
            <svg viewBox="0 0 20 20" className="h-4 w-4" fill="currentColor" aria-hidden="true">
              <path fillRule="evenodd" d="M10 18a8 8 0 1 0 0-16 8 8 0 0 0 0 16Zm3.857-9.809a.75.75 0 0 0-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 1 0-1.06 1.061l2.5 2.5a.75.75 0 0 0 1.137-.089l4-5.5Z" clipRule="evenodd" />
            </svg>
            Сервер восстановлен
          </div>
        )}
      </div>
    </div>
  );
}
